import {
  TEMPLATE_BLOCK_TYPES,
  TEMPLATE_FACETS,
  type TemplateFacetKey,
  type TemplateFacets,
} from '@webbuilder/schemas'
import { IconCheck } from '@tabler/icons-react'
import { useI18n } from '../lib/i18n'

// Category is edited by the form's own <select> (it is also a top-level column).
// Driven entirely by the taxonomy: a new value in @webbuilder/schemas shows up
// here automatically. `keys` picks which facets this instance shows, and in
// what order — the form splits them between what the AI needs and the rest.

type Props = {
  /** Las facetas a pintar, en este orden. */
  keys: readonly TemplateFacetKey[]
  facets: TemplateFacets
  /** Block-type chips only make sense for a repeatable component. */
  showBlockType: boolean
  onChange: (next: TemplateFacets) => void
}

const asArray = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? value : value ? [value] : []

export function TemplateFacetEditor({ keys, facets, showBlockType, onChange }: Props) {
  const { t } = useI18n()
  const setFacet = (key: TemplateFacetKey, value: string, multiple: boolean) => {
    const selected = asArray(facets[key])
    const next = selected.includes(value)
      ? selected.filter((entry) => entry !== value)
      : multiple ? [...selected, value] : [value]
    const merged: TemplateFacets = { ...facets }
    if (next.length > 0) (merged[key] as string | string[]) = multiple ? next : next[0]!
    else delete merged[key]
    onChange(merged)
  }

  const isActive = (key: TemplateFacetKey, value: string) => asArray(facets[key]).includes(value)

  return (
    <div className="dash-facet-editor">
      {keys.flatMap((key) => TEMPLATE_FACETS.filter((facet) => facet.key === key)).map((facet) => (
        <div className="dash-field" key={facet.key}>
          <span>{t(facet.labelKey)}</span>
          <div className="dash-chip-group">
            {facet.options.map((option) => {
              const active = isActive(facet.key, option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`dash-chip${active ? ' active' : ''}`}
                  title={t(option.hintKey)}
                  onClick={() => setFacet(facet.key, option.value, facet.multiple)}
                >
                  {active ? <IconCheck size={13} /> : null}{t(option.labelKey)}
                </button>
              )
            })}
          </div>
        </div>
      ))}

      {showBlockType ? (
        <div className="dash-field">
          <span>{t('templateFacets.blockType')}</span>
          <div className="dash-chip-group">
            {TEMPLATE_BLOCK_TYPES.map((option) => {
              const active = facets.blockType === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`dash-chip${active ? ' active' : ''}`}
                  title={t(option.hintKey)}
                  onClick={() => {
                    const next: TemplateFacets = { ...facets }
                    if (active) delete next.blockType
                    else next.blockType = option.value
                    onChange(next)
                  }}
                >
                  {active ? <IconCheck size={13} /> : null}{t(option.labelKey)}
                </button>
              )
            })}
          </div>
          <p className="dash-field-hint">{t('templateFacets.blockTypeHint')}</p>
        </div>
      ) : null}
    </div>
  )
}
